// ─── ChangelogPanel.jsx — searchable activity log side-panel ────────────────
// Shows every data-modifying action in reverse chronological order. Clicking
// a row navigates to the exact page + field and triggers the red-glow
// highlight via useFieldHighlight.
//
// Data source: useRecentChanges() (300 most recent audit rows, module-cached).
// Filtering: client-side on summary/path/from/to/email + area pills + user.

import { useState, useMemo, useRef, useEffect } from "react";
import { useTheme } from "../lib/ThemeContext.jsx";
import { SlidePanel } from "./ui.jsx";
import { useRecentChanges } from "../hooks/useRecentChanges.js";
import { ENTITY_TO_AREA } from "../lib/audit/fieldKeys.js";
import { humanizeEvent } from "../lib/audit/humanize.js";
import { FONT, RADIUS } from "../lib/theme.js";

// ── Area → page routing ────────────────────────────────────────────────────
const PAGE_MAP = {
  finance: "targets",
  marketplaces: "marketplaces",
  orders: "orders",
  recurring: "recurring",
  decisions: "planner",
  pools: "marketplaces",
};

// Special field-key overrides (some finance fields live on Dashboard, not Targets)
function resolveNavTarget(row) {
  const area = row.area || ENTITY_TO_AREA[row.entity_type] || "finance";
  const fieldKey = row.details?.fieldKey || "";
  let page = PAGE_MAP[area] || "dashboard";
  // Available Today lives on the Dashboard, not Targets
  if (fieldKey.startsWith("brand.") && fieldKey.includes("availableToday")) page = "dashboard";
  // Delivery overrides + order expenses → Cash Calendar
  if (row.entity_type === "delivery_override" || row.entity_type === "order_expense") page = "cashflow";
  return { page, params: { highlightFieldKey: fieldKey, ts: Date.now() } };
}

// ── Area filter config ─────────────────────────────────────────────────────
const AREAS = [
  { id: "finance",      label: "Finance",      icon: "💰" },
  { id: "marketplaces", label: "Markets",       icon: "🏪" },
  { id: "orders",       label: "Orders",        icon: "📦" },
  { id: "recurring",    label: "Recurring",     icon: "🔄" },
  { id: "decisions",    label: "Decisions",      icon: "📌" },
  { id: "pools",        label: "Pools",         icon: "📊" },
];

// ── Date grouping helpers ──────────────────────────────────────────────────
function dayKey(isoStr) {
  if (!isoStr) return "";
  return isoStr.slice(0, 10); // "2026-04-16"
}

function dayLabel(dk) {
  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  if (dk === today) return "Today";
  if (dk === yesterday) return "Yesterday";
  const d = new Date(dk + "T00:00:00");
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function timeStr(isoStr) {
  if (!isoStr) return "";
  const d = new Date(isoStr);
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

// ── Truncate long values for display ───────────────────────────────────────
function fmtVal(v) {
  if (v == null || v === "") return "—";
  const s = String(v);
  return s.length > 40 ? s.slice(0, 37) + "…" : s;
}

// ── Component ──────────────────────────────────────────────────────────────
export default function ChangelogPanel({ open, onClose, triggerRef, onNavigate, session }) {
  const T = useTheme();
  const { rows, loading, refresh } = useRecentChanges();
  const [search, setSearch] = useState("");
  const [areaFilter, setAreaFilter] = useState(null); // null = all
  const [userFilter, setUserFilter] = useState("");    // "" = all
  const searchRef = useRef(null);

  // Focus search on open
  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 200);
  }, [open]);

  // Mark as seen (for unread badge in navbar)
  useEffect(() => {
    if (open && rows.length > 0) {
      try { localStorage.setItem("sf_changelog_last_seen", rows[0].created_at); } catch {}
    }
  }, [open, rows]);

  // Unique users for the user filter dropdown
  const users = useMemo(() => {
    const set = new Set();
    for (const r of rows) if (r.email) set.add(r.email);
    return [...set].sort();
  }, [rows]);

  const currentEmail = session?.user?.email;

  // Apply filters
  const filtered = useMemo(() => {
    let result = rows;
    if (areaFilter) {
      result = result.filter(r => (r.area || ENTITY_TO_AREA[r.entity_type]) === areaFilter);
    }
    if (userFilter) {
      result = result.filter(r => r.email === userFilter);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(r => {
        const haystack = [
          r.summary, r.details?.path, String(r.details?.from ?? ""),
          String(r.details?.to ?? ""), r.email, r.entity_type,
        ].join(" ").toLowerCase();
        return haystack.includes(q);
      });
    }
    return result;
  }, [rows, areaFilter, userFilter, search]);

  // Group by day
  const grouped = useMemo(() => {
    const map = new Map();
    for (const r of filtered) {
      const dk = dayKey(r.created_at);
      if (!map.has(dk)) map.set(dk, []);
      map.get(dk).push(r);
    }
    return [...map.entries()]; // [["2026-04-16", [rows...]], ...]
  }, [filtered]);

  function handleRowClick(row) {
    const nav = resolveNavTarget(row);
    onNavigate?.(nav.page, nav.params);
    onClose?.();
  }

  return (
    <SlidePanel open={open} onClose={onClose} title="📋 Activity Log" triggerRef={triggerRef} width={420}>
      <div style={{ display: "flex", flexDirection: "column", gap: 10, fontSize: 13, fontFamily: FONT }}>

        {/* Search bar */}
        <input
          ref={searchRef}
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search changes..."
          style={{
            width: "100%", padding: "8px 12px", borderRadius: RADIUS.md,
            background: T.surface, border: `1px solid ${T.border}`,
            color: T.text, fontSize: 13, fontFamily: FONT, outline: "none",
          }}
        />

        {/* Area filter pills */}
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          <button
            onClick={() => setAreaFilter(null)}
            style={{
              padding: "4px 10px", borderRadius: RADIUS.pill, fontSize: 11,
              fontWeight: areaFilter === null ? 700 : 400, fontFamily: FONT,
              cursor: "pointer", border: "none",
              background: areaFilter === null ? T.accent + "22" : T.surface,
              color: areaFilter === null ? T.accent : T.textSub,
            }}
          >All</button>
          {AREAS.map(a => (
            <button
              key={a.id}
              onClick={() => setAreaFilter(areaFilter === a.id ? null : a.id)}
              style={{
                padding: "4px 10px", borderRadius: RADIUS.pill, fontSize: 11,
                fontWeight: areaFilter === a.id ? 700 : 400, fontFamily: FONT,
                cursor: "pointer", border: "none",
                background: areaFilter === a.id ? T.accent + "22" : T.surface,
                color: areaFilter === a.id ? T.accent : T.textSub,
              }}
            >{a.icon} {a.label}</button>
          ))}
        </div>

        {/* User filter */}
        {users.length > 1 && (
          <select
            value={userFilter}
            onChange={e => setUserFilter(e.target.value)}
            style={{
              padding: "6px 10px", borderRadius: RADIUS.sm,
              background: T.surface, border: `1px solid ${T.border}`,
              color: T.text, fontSize: 12, fontFamily: FONT,
            }}
          >
            <option value="">All users</option>
            {users.map(u => (
              <option key={u} value={u}>{u === currentEmail ? `${u} (you)` : u}</option>
            ))}
          </select>
        )}

        {/* Results count */}
        <div style={{ fontSize: 11, color: T.textSub }}>
          {loading ? "Loading..." : `${filtered.length} change${filtered.length !== 1 ? "s" : ""}`}
          {search && ` matching "${search}"`}
        </div>

        {/* Grouped list */}
        <div style={{ maxHeight: "calc(100vh - 280px)", overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}
          className="sf-scroll">
          {grouped.map(([dk, dayRows]) => (
            <div key={dk}>
              {/* Day header */}
              <div style={{
                fontSize: 11, fontWeight: 700, color: T.textSub,
                padding: "8px 0 4px", borderBottom: `1px solid ${T.border}`,
                letterSpacing: 0.3, textTransform: "uppercase",
                position: "sticky", top: 0, background: T.card, zIndex: 1,
              }}>{dayLabel(dk)}</div>

              {/* Rows */}
              {dayRows.map(row => {
                const isOther = row.email && row.email !== currentEmail;
                const op = row.details?.op || row.action || "update";
                const opColor = op === "add" ? T.green : op === "remove" ? T.red : T.accent;
                return (
                  <div
                    key={row.id}
                    onClick={() => handleRowClick(row)}
                    style={{
                      padding: "8px 10px", borderRadius: RADIUS.sm,
                      background: isOther ? opColor + "06" : "transparent",
                      border: `1px solid ${isOther ? opColor + "15" : T.border + "40"}`,
                      cursor: "pointer", marginTop: 4,
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = opColor + "12"}
                    onMouseLeave={e => e.currentTarget.style.background = isOther ? opColor + "06" : "transparent"}
                  >
                    {/* Header: time + user */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                      <span style={{ fontSize: 11, color: T.textSub }}>
                        {timeStr(row.created_at)}
                      </span>
                      <span style={{
                        fontSize: 10, fontWeight: isOther ? 700 : 400,
                        color: isOther ? opColor : T.textSub,
                      }}>
                        {row.email === currentEmail ? "You" : (row.email || "System")}
                      </span>
                    </div>

                    {/* Friendly sentence — same renderer as the full
                        Activity Log on the Profile page. No more raw
                        action names + [object Object]. */}
                    <div style={{ fontSize: 12, fontWeight: 600, color: T.text, lineHeight: 1.5 }}>
                      {humanizeEvent(row)}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          {!loading && filtered.length === 0 && (
            <div style={{ textAlign: "center", padding: "32px 16px", color: T.textSub, fontSize: 13 }}>
              {rows.length === 0 ? "No activity yet" : "No changes match your filters"}
            </div>
          )}
        </div>
      </div>
    </SlidePanel>
  );
}
